import {
    Enum_Flowpages_Display,
    FlowPageFragment,
} from '@graphql/generated/graphql';
import {
    DividerFlowPage,
    FlowPageWithLoader,
    RegistrationFlowPage,
    SellFlowPage,
    SuccessFlowPage,
    WelcomeFlowPage,
} from '../types';
import { EventTriggerSource } from '@enums';
import { Feature } from '@lib/posthog/types';
import { FeatureFlagKey } from '@lib/posthog/enums';

export const isWelcomeFlowPage = (
    flowPage: FlowPageFragment
): flowPage is WelcomeFlowPage => {
    return flowPage.Display === Enum_Flowpages_Display.Welcome;
};

export const isDividerFlowPage = (
    flowPage: FlowPageFragment
): flowPage is DividerFlowPage => {
    return flowPage.Display === Enum_Flowpages_Display.Divider;
};

export const isSuccessFlowPage = (
    flowPage: FlowPageFragment
): flowPage is SuccessFlowPage => {
    return flowPage.Display === Enum_Flowpages_Display.Success;
};

export const isSellFlowPage = (
    flowPage: FlowPageFragment
): flowPage is SellFlowPage => {
    return Boolean(
        flowPage.Display === Enum_Flowpages_Display.Sell &&
            flowPage.AdditionalProduct?.Additional_product
    );
};

export const isRegistrationFlowPage = (
    flowPage: FlowPageFragment
): flowPage is RegistrationFlowPage => {
    const elements = flowPage.Registration?.Form?.Elements;

    if (elements?.length !== 1) {
        return false;
    }

    if (elements[0].__typename !== 'ComponentFormTemplatesRegistration') {
        return false;
    }

    return true;
};

export const isFlowPageWithLoader = (
    flowPage: FlowPageFragment
): flowPage is FlowPageWithLoader => {
    return Boolean(flowPage.Loader);
};

export const isRegistrationRequired = ({
    flowPage,
    triggerSource,
    feature,
}: {
    flowPage: FlowPageFragment;
    triggerSource: EventTriggerSource;
    feature?: Feature<FeatureFlagKey.AllowOnboardingSkip>;
}): boolean => {
    const requiredByABTest =
        triggerSource === EventTriggerSource.ONBOARDING &&
        feature?.variant === 'disallow-skip';
    const requiredByCMS =
        isRegistrationFlowPage(flowPage) &&
        flowPage.Registration.Form.Elements[0].IsRequired;

    return requiredByABTest || requiredByCMS;
};

export const isFlowPageSkippable = ({
    isRegistrationRequired,
    flowCurrentStep,
    flowTotalSteps,
    flowPage,
}: {
    isRegistrationRequired: boolean;
    flowCurrentStep: number;
    flowTotalSteps: number;
    flowPage: FlowPageFragment;
}): boolean => {
    if (flowCurrentStep >= flowTotalSteps) {
        return false;
    }

    return (
        isSellFlowPage(flowPage) ||
        (isRegistrationFlowPage(flowPage) && !isRegistrationRequired)
    );
};
